import { action, computed, makeObservable, observable } from "mobx";
import { Model } from "../models/Model";
import { Command } from "../mvvm/commands/Command/Command";
import { DeckDef, SlideDef } from "./types";

export type PresentationMode = "present" | "overview" | "notes";

const clamp = (n: number, min: number, max: number) => Math.max(min, Math.min(max, n));

/**
 * Model prezentacji (MVVM/MobX). Trzyma stan i komendy nawigacji; widok binduje do niego klawiaturę,
 * pełny ekran i synchronizację okna prezentera. Treść slajdów wstrzykiwana jako DeckDef.
 */
export class PresentationModel extends Model {
  readonly deck: DeckDef;
  currentIndex: number;
  mode: PresentationMode = "present";
  isFullscreen = false;
  elapsedSec = 0;
  direction: 1 | -1 = 1;
  reducedMotion = false;

  constructor(deck: DeckDef, startIndex = 0) {
    super();
    this.deck = deck;
    this.currentIndex = clamp(startIndex, 0, Math.max(0, deck.slides.length - 1));
    if (typeof window !== "undefined" && typeof window.matchMedia === "function") {
      this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    }
    makeObservable(this, {
      currentIndex: observable,
      mode: observable,
      isFullscreen: observable,
      elapsedSec: observable,
      direction: observable,
      total: computed,
      current: computed,
      next: computed,
      progress: computed,
      setIndex: action,
      setMode: action,
      setFullscreen: action,
      tick: action,
      resetTimer: action
    });
  }

  async onInit(): Promise<void> {}

  get total(): number {
    return this.deck.slides.length;
  }
  get current(): SlideDef {
    return this.deck.slides[this.currentIndex];
  }
  get next(): SlideDef | undefined {
    return this.deck.slides[this.currentIndex + 1];
  }
  get progress(): number {
    return this.total ? (this.currentIndex + 1) / this.total : 0;
  }

  setIndex(index: number) {
    const target = clamp(index, 0, this.total - 1);
    this.direction = target >= this.currentIndex ? 1 : -1;
    this.currentIndex = target;
  }
  setMode(mode: PresentationMode) {
    this.mode = mode;
  }
  setFullscreen(value: boolean) {
    this.isFullscreen = value;
  }
  tick() {
    this.elapsedSec += 1;
  }
  resetTimer() {
    this.elapsedSec = 0;
  }

  readonly goNext = new Command(
    () => this.setIndex(this.currentIndex + 1),
    () => this.currentIndex < this.total - 1
  );
  readonly goPrev = new Command(
    () => this.setIndex(this.currentIndex - 1),
    () => this.currentIndex > 0
  );

  goTo = (index: number) => this.setIndex(index);
  toggleOverview = () => this.setMode(this.mode === "overview" ? "present" : "overview");
  toggleNotes = () => this.setMode(this.mode === "notes" ? "present" : "notes");
  present = () => this.setMode("present");
}
